import React, { useEffect, useMemo, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { fmtEUR, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { AlertTriangle, Plus, Phone, Mail, Home, ShieldCheck, ShieldOff, CalendarClock, Wrench, Camera, MessageSquare, Check } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UnitPicker } from "../components/tickets/UnitPicker";

const COL = { nuovo: "bg-sky-500", assegnato: "bg-indigo-500", appuntamento: "bg-amber-500", in_lavorazione: "bg-orange-600", risolto: "bg-emerald-600", chiuso: "bg-slate-500" };

const Garanzia = ({ g }) => {
  if (!g) return null;
  const ok = g.in_garanzia === true;
  const parz = g.in_garanzia === "solo_gravi";
  return (
    <div className={`flex items-start gap-2 rounded-lg border p-3 text-xs ${ok ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20" : parz ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20" : "border-rose-300 bg-rose-50 dark:bg-rose-950/20"}`} data-testid="ticket-garanzia">
      {ok ? <ShieldCheck className="mt-0.5 h-4 w-4 text-emerald-600" /> : <ShieldOff className="mt-0.5 h-4 w-4 text-rose-600" />}
      <div>
        <div className="font-medium">{g.tipo || "Garanzia da verificare"} <span className="font-normal text-muted-foreground">(riservato: non comunicare al cliente)</span></div>
        <div className="text-muted-foreground">{g.nota}</div>
      </div>
    </div>
  );
};

const NuovoTicket = ({ d, onDone, operationId = "" }) => {
  const [f, setF] = useState({ cliente: "", telefono: "", email: "", indirizzo: "", interno: "", titolo: "", descrizione: "", stanza: "", categoria: "", priorita: "media", canale: "telefono", operation_id: operationId, unit_id: "" });
  const [busy, setBusy] = useState(false);
  const s = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const salva = async () => {
    if (!f.descrizione.trim()) return toast.error("Descrivi il problema");
    setBusy(true);
    try { const r = await api.post("/tickets", f); toast.success(`Ticket ${r.data.codice} creato${r.data.da_abbinare ? " — appartamento da abbinare" : ""}`); onDone(r.data); }
    catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="ticket-new-form">
      <Card className="space-y-2 p-3">
        <div className="text-xs font-medium text-muted-foreground">Appartamento (scegli dalle nostre operazioni, oppure lascia vuoto: proviamo ad abbinarlo da cognome e indirizzo)</div>
        <UnitPicker operationId={f.operation_id} unitId={f.unit_id} onChange={(v) => setF((p) => ({ ...p, ...v }))} />
      </Card>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1"><Label className="text-xs">Cliente (cognome)</Label><Input value={f.cliente} onChange={(e) => s("cliente", e.target.value)} data-testid="tnew-cliente" /></div>
        <div className="space-y-1"><Label className="text-xs">Telefono</Label><Input value={f.telefono} onChange={(e) => s("telefono", e.target.value)} data-testid="tnew-telefono" /></div>
        <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={f.email} onChange={(e) => s("email", e.target.value)} data-testid="tnew-email" /></div>
        <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Indirizzo</Label><Input value={f.indirizzo} onChange={(e) => s("indirizzo", e.target.value)} placeholder="Via Paterno 19, Milano" data-testid="tnew-indirizzo" /></div>
        <div className="space-y-1"><Label className="text-xs">Interno / piano</Label><Input value={f.interno} onChange={(e) => s("interno", e.target.value)} data-testid="tnew-interno" /></div>
      </div>
      <div className="space-y-1"><Label className="text-xs">Problema</Label><Textarea value={f.descrizione} onChange={(e) => s("descrizione", e.target.value)} placeholder="Perde il sifone del bagno padronale…" data-testid="tnew-descrizione" /></div>
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="space-y-1"><Label className="text-xs">Stanza</Label><Input value={f.stanza} onChange={(e) => s("stanza", e.target.value)} data-testid="tnew-stanza" /></div>
        <div className="space-y-1"><Label className="text-xs">Categoria</Label>
          <Select value={f.categoria} onValueChange={(v) => s("categoria", v)}><SelectTrigger data-testid="tnew-categoria"><SelectValue placeholder="scegli…" /></SelectTrigger>
            <SelectContent>{(d?.categorie || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1"><Label className="text-xs">Priorità</Label>
          <Select value={f.priorita} onValueChange={(v) => s("priorita", v)}><SelectTrigger data-testid="tnew-priorita"><SelectValue /></SelectTrigger>
            <SelectContent>{(d?.priorita || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1"><Label className="text-xs">Arrivato da</Label>
          <Select value={f.canale} onValueChange={(v) => s("canale", v)}><SelectTrigger data-testid="tnew-canale"><SelectValue /></SelectTrigger>
            <SelectContent>{["telefono", "whatsapp", "email", "app"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <DialogFooter><Button onClick={salva} disabled={busy} data-testid="tnew-save">Apri ticket</Button></DialogFooter>
    </div>
  );
};

const Dettaglio = ({ id, d, onChange }) => {
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [pick, setPick] = useState({ operation_id: "", unit_id: "" });
  const [msg, setMsg] = useState("");
  const [costo, setCosto] = useState("");
  const [esito, setEsito] = useState("");
  const load = async () => { try { const r = await api.get(`/tickets/${id}`); setT(r.data); } catch (e) { toast.error(fmtErr(e)); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const upd = async (body) => { try { await api.put(`/tickets/${id}`, body); await load(); onChange(); toast.success("Aggiornato"); } catch (e) { toast.error(fmtErr(e)); } };
  if (!t) return <div className="p-6 text-sm text-muted-foreground">Caricamento…</div>;
  return (
    <div className="space-y-4" data-testid="ticket-detail">
      <div className="flex flex-wrap items-center gap-2">
        <Badge className={`${COL[t.stato]} text-white`}>{t.stato_label}</Badge>
        <Badge variant="outline">{t.priorita}</Badge>
        <span className="text-xs text-muted-foreground">{t.codice} · aperto il {fmtDate(t._created)} · via {t.canale}</span>
        {t.attenzione && <Badge className="bg-rose-600 text-white"><AlertTriangle className="mr-1 h-3 w-3" />{t.attenzione}</Badge>}
      </div>
      <div className="text-base font-semibold">{t.titolo}</div>
      <p className="whitespace-pre-wrap text-sm text-muted-foreground">{t.descrizione}</p>

      <div className="grid gap-3 sm:grid-cols-2">
        <Card className="space-y-1 p-3 text-sm">
          <div className="text-xs font-medium text-muted-foreground">Cliente</div>
          <div>{t.cliente || "—"}</div>
          {t.telefono && <div className="flex items-center gap-1 text-xs"><Phone className="h-3 w-3" />{t.telefono}</div>}
          {t.email && <div className="flex items-center gap-1 text-xs"><Mail className="h-3 w-3" />{t.email}</div>}
        </Card>
        <Card className="space-y-1 p-3 text-sm">
          <div className="text-xs font-medium text-muted-foreground">Appartamento</div>
          {t.da_abbinare ? (
            <div className="space-y-2">
              <div className="text-rose-600">Da abbinare</div>
              {(t.candidati || []).map((c) => (
                <button key={c.unit_id} onClick={() => upd({ unit_id: c.unit_id })} className="block w-full rounded border p-2 text-left text-xs hover:bg-muted" data-testid={`ticket-abbina-${c.unit_id}`}>
                  <b>{c.unita}</b> — {c.operazione} · {c.cliente || "cliente non indicato"} {c.piano ? `· piano ${c.piano}` : ""}
                </button>
              ))}
              {!(t.candidati || []).length && <div className="text-xs text-muted-foreground">Nessuna corrispondenza automatica</div>}
              <div className="text-xs text-muted-foreground">Oppure scegli dalle nostre operazioni:</div>
              <UnitPicker operationId={pick.operation_id} unitId={pick.unit_id} compact onChange={(v) => { setPick(v); if (v.unit_id) upd({ unit_id: v.unit_id }); }} />
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Home className="h-3 w-3" />
              <button className="underline" onClick={() => nav(`/unita/${t.unit_id}`)} data-testid="ticket-goto-unit">{t.unita}</button> —
              <button className="underline" onClick={() => nav(`/operazioni/${t.operation_id}?tab=assistenza`)} data-testid="ticket-goto-op">{t.operazione}</button>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => upd({ unit_id: null })} data-testid="ticket-cambia-unit">cambia</Button>
            </div>
          )}
          {t.stanza && <div className="text-xs text-muted-foreground">Stanza: {t.stanza}</div>}
        </Card>
      </div>

      <Garanzia g={t.garanzia} />

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1"><Label className="text-xs">Stato</Label>
          <Select value={t.stato} onValueChange={(v) => upd({ stato: v })}><SelectTrigger data-testid="ticket-stato"><SelectValue /></SelectTrigger>
            <SelectContent>{(d?.stati || []).map((s) => <SelectItem key={s.k} value={s.k}>{s.l}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1"><Label className="text-xs">Categoria</Label>
          <Select value={t.categoria || ""} onValueChange={(v) => upd({ categoria: v })}><SelectTrigger data-testid="ticket-categoria"><SelectValue placeholder="scegli…" /></SelectTrigger>
            <SelectContent>{(d?.categorie || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1"><Label className="text-xs">Appuntamento</Label>
          <Input type="date" value={(t.appuntamento || "").slice(0, 10)} onChange={(e) => upd({ appuntamento: e.target.value })} data-testid="ticket-appuntamento" /></div>
      </div>

      <Card className="space-y-2 p-3">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground"><Wrench className="h-3 w-3" />Chi interviene</div>
        {t.fornitore ? <div className="text-sm">{t.fornitore} <Button variant="ghost" size="sm" onClick={() => upd({ supplier_id: "" })} data-testid="ticket-rimuovi-fornitore">cambia</Button></div> : (
          <div className="flex flex-wrap gap-2">
            {(t.fornitori_suggeriti || []).map((s) => (
              <button key={s.supplier_id} onClick={() => upd({ supplier_id: s.supplier_id })} className="rounded-full border px-3 py-1 text-xs hover:bg-muted" data-testid={`ticket-assegna-${s.supplier_id}`}>
                {s.fornitore}{s.preferito ? " ⭐" : ""} <span className="text-muted-foreground">· {s.categorie}</span>
              </button>
            ))}
            {!(t.fornitori_suggeriti || []).length && <span className="text-xs text-muted-foreground">Nessun fornitore con questa categoria: completa le categorie in Fornitori → Preferiti e ordini</span>}
          </div>
        )}
      </Card>

      {!!(t.foto || []).length && (
        <Card className="space-y-2 p-3">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground"><Camera className="h-3 w-3" />Foto ({t.foto.length})</div>
          <div className="flex flex-wrap gap-2">
            {t.foto.map((f) => <a key={f.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${f.id}/download`} target="_blank" rel="noreferrer" className="text-xs underline" data-testid={`ticket-foto-${f.id}`}>{f.nome}</a>)}
          </div>
        </Card>
      )}

      <Card className="space-y-2 p-3">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground"><MessageSquare className="h-3 w-3" />Storico messaggi</div>
        <div className="max-h-48 space-y-2 overflow-y-auto">
          {(t.messaggi || []).map((m, i) => (
            <div key={i} className={`rounded-lg border p-2 text-xs ${m.da === "cliente" ? "bg-muted" : "bg-sky-50 dark:bg-sky-950/20"}`}>
              <div className="text-[10px] text-muted-foreground">{m.da === "cliente" ? "Cliente" : "Noi"} · {m.canale} · {fmtDate(m.at)}</div>
              {m.testo}
            </div>
          ))}
          {!(t.messaggi || []).length && <div className="text-xs text-muted-foreground">Nessun messaggio</div>}
        </div>
        <div className="flex gap-2">
          <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Annota cosa hai detto al cliente…" data-testid="ticket-msg-input" />
          <Button size="sm" onClick={async () => { if (!msg.trim()) return; await api.post(`/tickets/${id}/messaggi`, { testo: msg }); setMsg(""); load(); }} data-testid="ticket-msg-send">Annota</Button>
        </div>
      </Card>

      {t.stato !== "chiuso" ? (
        <Card className="space-y-2 p-3">
          <div className="text-xs font-medium text-muted-foreground">Chiudi la pratica</div>
          <div className="grid gap-2 sm:grid-cols-3">
            <Input value={costo} onChange={(e) => setCosto(e.target.value)} placeholder="Costo sostenuto €" data-testid="ticket-costo" />
            <Input value={esito} onChange={(e) => setEsito(e.target.value)} placeholder="Esito / lavoro fatto" className="sm:col-span-2" data-testid="ticket-esito" />
          </div>
          <div className="text-[11px] text-muted-foreground">Se indichi un costo verrà registrato sull'operazione come «assistenza post-vendita».</div>
          <Button size="sm" onClick={async () => {
            try { const r = await api.post(`/tickets/${id}/chiudi`, { costo: parseFloat(costo || 0), esito }); toast.success(r.data.movimento_creato ? "Chiuso e costo registrato sull'operazione" : "Ticket chiuso"); load(); onChange(); }
            catch (e) { toast.error(fmtErr(e)); }
          }} data-testid="ticket-chiudi"><Check className="mr-1 h-4 w-4" />Chiudi ticket</Button>
        </Card>
      ) : (
        <Card className="p-3 text-sm" data-testid="ticket-chiuso-box">Chiuso{t.costo ? ` — costo ${fmtEUR(t.costo)}${t.movement_id ? " (registrato sull'operazione)" : ""}` : ""}{t.esito ? ` · ${t.esito}` : ""}</Card>
      )}
    </div>
  );
};

const Clienti = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/clienti").then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  return (
    <Card className="divide-y" data-testid="clienti-list">
      {items.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm" data-testid={`cliente-${c.id}`}>
          <div>
            <div className="font-medium">{c.nome}</div>
            <div className="text-xs text-muted-foreground">{[c.telefono, c.email].filter(Boolean).join(" · ") || "nessun contatto"}{c.unita?.length ? ` · ${c.unita.join(", ")}` : ""}</div>
          </div>
          <Badge variant="outline">{c.n_ticket} ticket</Badge>
        </div>
      ))}
      {!items.length && <div className="p-6 text-center text-sm text-muted-foreground">Nessun cliente ancora registrato: si crea da solo al primo ticket</div>}
    </Card>
  );
};

export default function Tickets() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const canWrite = ["super_admin", "amministrazione", "project_manager", "responsabile_cantiere"].includes(user?.role);
  const [d, setD] = useState(null);
  const [stato, setStato] = useState("");
  const [cerca, setCerca] = useState("");
  const [opId, setOpId] = useState(sp.get("operation_id") || "");
  const [ops, setOps] = useState([]);
  const [sel, setSel] = useState(sp.get("ticket") || null);
  const [nuovo, setNuovo] = useState(false);

  const load = async (opts = {}) => {
    try { const r = await api.get("/tickets", { params: { stato: (opts.stato ?? stato) || undefined, cerca: cerca || undefined, operation_id: (opts.opId ?? opId) || undefined } }); setD(r.data); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); api.get("/operations").then((r) => setOps(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);
  const items = d?.items || [];
  const attenzione = useMemo(() => items.filter((i) => i.attenzione), [items]);

  return (
    <div className="space-y-4 p-1" data-testid="tickets-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Assistenza post-vendita</h1>
          <p className="text-sm text-muted-foreground">Le segnalazioni dei clienti che hanno comprato: abbinate all'appartamento, con foto, tecnico e costi.</p>
        </div>
        {canWrite && (
          <Dialog open={nuovo} onOpenChange={setNuovo}>
            <DialogTrigger asChild><Button data-testid="ticket-new-btn"><Plus className="mr-2 h-4 w-4" />Nuovo ticket</Button></DialogTrigger>
            <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>Nuova segnalazione cliente</DialogTitle></DialogHeader>
              <NuovoTicket d={d} operationId={opId} onDone={(t) => { setNuovo(false); load(); setSel(t.id); }} /></DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs defaultValue="bacheca">
        <TabsList>
          <TabsTrigger value="bacheca" data-testid="ttab-bacheca">Bacheca</TabsTrigger>
          <TabsTrigger value="clienti" data-testid="ttab-clienti">Clienti</TabsTrigger>
        </TabsList>
        <TabsContent value="bacheca" className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input value={cerca} onChange={(e) => setCerca(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cerca codice, cliente, telefono…" className="h-9 max-w-xs" data-testid="tickets-search" />
            <Button variant="outline" size="sm" onClick={() => load()} data-testid="tickets-search-btn">Cerca</Button>
            <select value={opId} onChange={(e) => { setOpId(e.target.value); load({ opId: e.target.value }); }} className="h-9 rounded-md border bg-background px-2 text-xs" data-testid="tickets-filter-op">
              <option value="">Tutte le operazioni</option>
              {ops.map((o) => <option key={o.id} value={o.id}>{o.nome_operazione}</option>)}
            </select>
            <button onClick={() => { setStato(""); load({ stato: "" }); }} className={`rounded-full border px-3 py-1 text-xs ${!stato ? "bg-foreground text-background" : ""}`} data-testid="tfilter-tutti">tutti ({items.length})</button>
            {(d?.stati || []).map((s) => (
              <button key={s.k} onClick={() => { setStato(s.k); load({ stato: s.k }); }} className={`rounded-full border px-3 py-1 text-xs ${stato === s.k ? "bg-foreground text-background" : ""}`} data-testid={`tfilter-${s.k}`}>
                {s.l} ({d?.per_stato?.[s.k] || 0})
              </button>
            ))}
            {d && <Badge variant="outline" className="ml-auto" data-testid="tickets-kpi">{d.n_aperti} aperti · {d.n_attenzione} da attenzionare · costi {fmtEUR(d.costo_totale)}</Badge>}
          </div>

          {!!attenzione.length && (
            <Card className="border-rose-300 bg-rose-50 p-3 dark:bg-rose-950/20" data-testid="tickets-attenzione">
              <div className="flex items-center gap-2 text-sm font-medium text-rose-700"><AlertTriangle className="h-4 w-4" />Da attenzionare ({attenzione.length})</div>
              <div className="mt-2 space-y-1 text-xs">
                {attenzione.slice(0, 6).map((t) => (
                  <button key={t.id} onClick={() => setSel(t.id)} className="block text-left hover:underline" data-testid={`tatt-${t.id}`}>
                    {t.codice} — {t.cliente || "cliente ?"} · {t.titolo}: <b>{t.attenzione}</b>
                  </button>
                ))}
              </div>
            </Card>
          )}

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {items.map((t) => (
              <Card key={t.id} className="cursor-pointer space-y-2 p-4 transition-shadow hover:shadow-md" onClick={() => setSel(t.id)} data-testid={`ticket-card-${t.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <Badge className={`${COL[t.stato]} text-white`}>{t.stato_label}</Badge>
                  <span className="text-xs text-muted-foreground">{t.codice}</span>
                </div>
                <div className="font-medium leading-snug">{t.titolo}</div>
                <div className="text-xs text-muted-foreground">{t.cliente || "cliente ?"} · {t.unita || t.indirizzo || "appartamento da abbinare"}</div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {t.categoria && <Badge variant="outline">{t.categoria}</Badge>}
                  {t.priorita === "alta" && <Badge className="bg-rose-600 text-white">urgente</Badge>}
                  {t.appuntamento && <span className="flex items-center gap-1 text-muted-foreground"><CalendarClock className="h-3 w-3" />{fmtDate(t.appuntamento)}</span>}
                  {t.fornitore && <span className="flex items-center gap-1 text-muted-foreground"><Wrench className="h-3 w-3" />{t.fornitore}</span>}
                </div>
                {t.attenzione && <div className="flex items-center gap-1 text-xs text-rose-600"><AlertTriangle className="h-3 w-3" />{t.attenzione}</div>}
              </Card>
            ))}
            {!items.length && <Card className="p-8 text-center text-sm text-muted-foreground md:col-span-2 xl:col-span-3">Nessun ticket</Card>}
          </div>
        </TabsContent>
        <TabsContent value="clienti"><Clienti /></TabsContent>
      </Tabs>

      <Dialog open={!!sel} onOpenChange={() => setSel(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle>Pratica di assistenza</DialogTitle></DialogHeader>
          {sel && <Dettaglio id={sel} d={d} onChange={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
